import { Injectable, NgZone, Inject } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { environment } from 'src/environments/environment';
import { Observable, Observer, Subject } from 'rxjs';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
declare let davinci: any;

/**
 * The interface for the data returned by DaVinci on success.
 * This should match the structure of your flow's success response.
 */
export interface DaVinciSuccessResponse {
  access_token?: string;
  refresh_token?: string;
  additionalProperties?: {
    [key: string]: any;
  };
}

/**
 * Defines the configuration required to start a DaVinci flow.
 */
export interface DaVinciFlowConfig {
  policyId: string;
  targetElement: HTMLElement;
  /**
   * An optional callback that fires inside the Angular zone just before the
   * DaVinci widget is rendered. Ideal for hiding loading indicators.
   */
  onReady?: () => void;
  /**
   * An optional object to pass initial data into the DaVinci flow.
   * This is used for challenge flows like password reset.
   */
  flowInputData?: { [key: string]: any };
}

@Injectable({
  providedIn: 'root'
})
export class DavinciLoadWidgetService {
  private scriptsLoaded = false;
  private scriptLoadingSubject = new Subject<void>();

  constructor(
    private ngZone: NgZone,
    @Inject(DOCUMENT) private document: Document
  ) { }

  /**
   * The main public method to render a DaVinci flow.
   */
  public renderFlow(config: DaVinciFlowConfig): Observable<DaVinciSuccessResponse> {
    return new Observable((observer: Observer<DaVinciSuccessResponse>) => {
      this.loadScripts()
        .then(() => this.startFlow(config, observer))
        .catch(err => {
          this.ngZone.run(() => observer.error(err));
        });
    });
  }

  /**
   * Kicks off the DaVinci flow by fetching an SDK token and calling the skRenderScreen method.
   */
  private async startFlow(config: DaVinciFlowConfig, observer: Observer<DaVinciSuccessResponse>): Promise<void> {
    const { ping_companyId: companyId, ping_skApiKey: skApiKey } = environment;
    const tokenURL = 'https://orchestrate-api.pingone.com';
    const flowURL = 'https://auth.pingone.com/';

    if (!companyId || !config.policyId || !skApiKey) {
      return observer.error('DaVinci configuration is missing in the environment file.');
    }

    try {
      const response = await fetch(`${tokenURL}/v1/company/${companyId}/sdktoken`, {
        method: 'GET',
        headers: { 'X-SK-API-KEY': skApiKey }
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch SDK token: ${response.statusText}`);
      }

      const responseData = await response.json();
      const accessToken = responseData.access_token;

      if (!accessToken) {
        throw new Error('SDK token was not found in the API response.');
      }

      // Fire the onReady callback within NgZone before rendering the widget.
      if (config.onReady) {
        this.ngZone.run(() => config.onReady());
      }

      const props = {
        config: {
          method: 'runFlow',
          apiRoot: flowURL,
          accessToken,
          companyId,
          policyId: config.policyId,
          // Use 'parameters' as the key to pass data into the flow.
          ...(config.flowInputData && { parameters: config.flowInputData })
        },
        useModal: false,
        successCallback: (resp: DaVinciSuccessResponse) => this.ngZone.run(() => {
          observer.next(resp);
          observer.complete();
        }),
        errorCallback: (err: any) => this.ngZone.run(() => {
          observer.error(err);
        }),
      };

      davinci.skRenderScreen(config.targetElement, props);

    } catch (error) {
      this.ngZone.run(() => observer.error(error));
    }
  }

  /**
   * Dynamically loads the external davinci.js script.
   */
  private loadScripts(): Promise<void> {
    if (this.scriptsLoaded) {
      return Promise.resolve();
    }

    if (this.scriptLoadingSubject.observers.length > 0) {
      return new Promise(resolve => this.scriptLoadingSubject.subscribe({ next: () => resolve() }));
    }

    return new Promise((resolve, reject) => {
      this.scriptLoadingSubject.subscribe({
        next: () => resolve(),
        error: (e) => reject(e)
      });

      const script = this.document.createElement('script');
      script.src = 'https://assets.pingone.com/davinci/latest/davinci.js';
      script.type = 'text/javascript';
      script.async = true;

      script.onload = () => {
        this.scriptsLoaded = true;
        this.scriptLoadingSubject.next();
        this.scriptLoadingSubject.complete();
      };

      script.onerror = (err) => {
        this.scriptLoadingSubject.error(err);
        this.scriptLoadingSubject.complete();
        reject(err);
      };

      this.document.head.appendChild(script);
    });
  }
}
